import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { hostAPI } from '../services/api';
import { ShieldCheck, Loader2, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { label: '1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: '1 number', test: (p) => /\d/.test(p) },
  { label: '1 special character', test: (p) => /[!@#$%^&*()_+\-=[\]{};:'"\\|,.<>/?~`]/.test(p) },
];

const HostSetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const rulePass = PASSWORD_RULES.map((r) => r.test(password));
  const allPass = rulePass.every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing setup token. Please log in via OTP again.');
      navigate('/host/login');
      return;
    }
    if (!allPass) {
      toast.error('Password does not meet all the requirements');
      return;
    }
    if (password !== confirm) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      setLoading(true);
      const res = await hostAPI.setPassword({ token, password });
      if (res.success) {
        toast.success('Password set successfully! Welcome to your dashboard.');
        navigate('/host/dashboard');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to set password. Please try again.';
      toast.error(msg);
      if (err.response?.status === 400) {
        // Token invalid/expired
        setTimeout(() => navigate('/host/login'), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-md">
        <Card className="shadow-xl" data-testid="host-set-password-card">
          <CardHeader className="text-center">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">Set Your Password</CardTitle>
            <CardDescription>
              For added security, please create a password. You'll be able to log in faster next time.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pr-10"
                    data-testid="host-set-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    tabIndex={-1}
                    data-testid="host-set-password-toggle"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2"
                  data-testid="host-set-password-confirm"
                />
              </div>

              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                {PASSWORD_RULES.map((rule, i) => (
                  <div key={rule.label} className="flex items-center text-xs">
                    {rulePass[i] ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-600 mr-2" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-gray-300 mr-2" />
                    )}
                    <span className={rulePass[i] ? 'text-green-700' : 'text-gray-500'}>{rule.label}</span>
                  </div>
                ))}
              </div>

              <Button
                type="submit"
                disabled={loading || !allPass || password !== confirm}
                className="w-full py-6 bg-[#7CB342] hover:bg-[#6da036] text-white"
                data-testid="host-set-password-submit"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
                ) : 'Set Password & Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostSetPasswordPage;
